import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  Box,
  Grid,
  Typography,
  TextField,
} from "@material-ui/core";
import {
  Equalizer,
  PanTool,
  TurnedIn,
  GroupAdd,
  Update,
  Comment as CommentIcon,
} from "@material-ui/icons";

import SectionCard from "./SectionCard";
import MultiFileManager from "../../File/MultiFileManager/MultiFileManager";
import { groupMembersByRole, getRoleConfig } from "../../../constants/councilRoles";
import { getCouncilHistory } from "../../../reducer/internalResearch/action";
import {
  INT_COUNCIL_TYPE_SELECTION,
  INT_COUNCIL_TYPE_MIDTERM,
  INT_COUNCIL_TYPE_FINAL,
  INT_COUNCIL_ASSIGNMENT_STATUS_LABEL,
} from "../../../constants/internalResearchStatus";

// InternalResearchHistory — read-only TIMELINE toàn lifecycle đề tài Cơ sở.
// Mỗi section render conditional: chỉ hiện nếu phase tương ứng có data.
// Đồng bộ pattern với StudentProject/utils/StudentResearchHistory.
export default function InternalResearchHistory({ detail }) {
  // Các lần hội đồng trước đó (đã bị thay thế khi admin tạo lại hội đồng) —
  // riêng phần này cần tự fetch vì không nằm trong `detail` (chỉ có dòng
  // hiện tại qua detail.councilAssignments).
  const [pastRounds, setPastRounds] = useState({});

  useEffect(() => {
    let cancelled = false;
    if (!detail?.id) return undefined;
    Promise.all(
      [INT_COUNCIL_TYPE_SELECTION, INT_COUNCIL_TYPE_MIDTERM, INT_COUNCIL_TYPE_FINAL].map((type) =>
        getCouncilHistory(detail.id, type).then((rows) => [type, rows || []])
      )
    ).then((entries) => {
      if (cancelled) return;
      setPastRounds(Object.fromEntries(entries));
    });
    return () => {
      cancelled = true;
    };
  }, [detail?.id]);

  if (!detail) return null;

  const renderFileList = (raw) => {
    const urls = raw ? String(raw).split("||").filter(Boolean) : [];
    return (
      <MultiFileManager
        fileUrls={urls}
        setFileUrls={() => {}}
        newFiles={[]}
        setNewFiles={() => {}}
        disabled={true}
      />
    );
  };

  const councils = detail.councilAssignments || [];
  const selectionCouncil = councils.find((a) => a.councilType === INT_COUNCIL_TYPE_SELECTION);
  const midtermCouncil   = councils.find((a) => a.councilType === INT_COUNCIL_TYPE_MIDTERM);
  const finalCouncil     = councils.find((a) => a.councilType === INT_COUNCIL_TYPE_FINAL);

  const progressReport = detail.progressReport;
  const hasProgress    = !!progressReport;
  const hasFinalReport = !!detail.finalReportFileLink;
  const hasStopRequest = !!detail.leaveReason;
  const hasExtension   = !!detail.extensionRequestReason || !!detail.extendedUntilDate;

  // Render phần thân dùng chung cho cả hội đồng hiện tại lẫn 1 lần trong lịch sử
  // (members theo role + nhận xét + file) — chỉ khác nhau ở khung bọc ngoài.
  const renderCouncilBody = (council) => {
    const grouped = groupMembersByRole(council.members || []);
    return (
      <>
        {council.reviewComment && (
          <Box marginBottom={2} padding="8px 12px" bgcolor="#fafafa" borderRadius={4} border="solid 1px #e0e0e0">
            <Typography variant="caption" color="textSecondary"><b>Nhận xét hội đồng:</b></Typography>
            <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>{council.reviewComment}</Typography>
          </Box>
        )}
        {Object.entries(grouped).map(([role, mems]) => {
          if (!mems || mems.length === 0) return null;
          const cfg = getRoleConfig(role);
          return (
            <Box key={role} marginBottom={1}>
              <Typography variant="body2"><b>{cfg?.label || role}:</b></Typography>
              {mems.map((m, i) => (
                <Typography key={i} variant="body2" style={{ paddingLeft: 16 }}>
                  {m.name || "—"} {m.degree ? `(${m.degree})` : ""}
                </Typography>
              ))}
            </Box>
          );
        })}
        {council.minutesFileLink && (
          <Box marginTop={2}>
            <Typography variant="body2" gutterBottom><b>Biên bản:</b></Typography>
            {renderFileList(council.minutesFileLink)}
          </Box>
        )}
        {council.evaluationFileLink && (
          <Box marginTop={2}>
            <Typography variant="body2" gutterBottom><b>Phiếu đánh giá:</b></Typography>
            {renderFileList(council.evaluationFileLink)}
          </Box>
        )}
      </>
    );
  };

  // Render 1 hội đồng (selection/midterm/final) — hiện tại + các lần trước
  // (đã bị thay thế, lấy từ pastRounds[councilType]).
  const renderCouncil = (label, council, councilTypeKey) => {
    const past = pastRounds[councilTypeKey] || [];
    if (!council && past.length === 0) return null;
    return (
      <SectionCard
        key={`council-${councilTypeKey}`}
        icon={<GroupAdd />}
        title={
          council ? (
            <Box display="flex" alignItems="center" gridGap={12}>
              <span>{label}</span>
              <span style={{ fontSize: 13, color: "#666" }}>
                ({INT_COUNCIL_ASSIGNMENT_STATUS_LABEL[council.status] || council.status})
              </span>
            </Box>
          ) : label
        }
      >
        {council && renderCouncilBody(council)}
        {past.length > 0 && (
          <Box marginTop={council ? 3 : 0}>
            <Typography variant="body2" style={{ marginBottom: 8 }}>
              <b>Lịch sử các lần trước ({past.length}):</b>
            </Typography>
            {past.map((entry) => (
              <Box
                key={`past-${councilTypeKey}-${entry.round}`}
                marginBottom={2}
                padding="8px 12px"
                border="dashed 1px #ccc"
                borderRadius={4}
              >
                <Typography variant="body2" style={{ marginBottom: 6 }}>
                  <b>Lần {entry.round}</b>{" "}
                  <span style={{ color: "#666" }}>
                    ({INT_COUNCIL_ASSIGNMENT_STATUS_LABEL[entry.status] || entry.status})
                  </span>
                </Typography>
                {renderCouncilBody(entry)}
              </Box>
            ))}
          </Box>
        )}
      </SectionCard>
    );
  };

  return (
    <>
      {/* HĐ#1 — Tuyển chọn */}
      {renderCouncil("Hội đồng tuyển chọn (HĐ#1)", selectionCouncil, INT_COUNCIL_TYPE_SELECTION)}

      {/* BCTĐ (chỉ 2y) */}
      {hasProgress && (
        <SectionCard icon={<Equalizer />} title="Báo cáo tiến độ (BCTĐ)">
          {progressReport.submittedAt && (
            <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 12 }}>
              Đã nộp: {dayjs(progressReport.submittedAt).format("DD/MM/YYYY HH:mm")}
            </Typography>
          )}
          {progressReport.reviewComment && (
            <Box marginBottom={2} padding="8px 12px" bgcolor="#fafafa" borderRadius={4} border="solid 1px #e0e0e0">
              <Typography variant="caption" color="textSecondary"><b>Nhận xét hội đồng:</b></Typography>
              <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>{progressReport.reviewComment}</Typography>
            </Box>
          )}
          <Grid container spacing={2}>
            {[
              { label: "1. Công việc đã hoàn thành", value: progressReport.completedWork },
              { label: "2. Đánh giá tiến độ",        value: progressReport.evaluation },
              { label: "3. Kế hoạch tiếp theo",      value: progressReport.nextPlan },
              { label: "4. Kiến nghị",               value: progressReport.recommendation },
            ].map((f, i) => (
              <Grid item xs={12} key={i}>
                <Typography variant="body2" style={{ marginBottom: 6 }}><b>{f.label}</b></Typography>
                <TextField
                  fullWidth multiline minRows={2} variant="outlined" disabled
                  value={f.value || "(chưa nhập)"}
                />
              </Grid>
            ))}
            {progressReport.fileLink && (
              <Grid item xs={12}>
                <Typography variant="body2" style={{ marginBottom: 6 }}><b>File đính kèm</b></Typography>
                {renderFileList(progressReport.fileLink)}
              </Grid>
            )}
          </Grid>
        </SectionCard>
      )}

      {/* HĐ#2 — Đánh giá tiến độ */}
      {renderCouncil("Hội đồng đánh giá tiến độ (HĐ#2)", midtermCouncil, INT_COUNCIL_TYPE_MIDTERM)}

      {/* Báo cáo tổng kết */}
      {hasFinalReport && (
        <SectionCard icon={<TurnedIn />} title="Báo cáo tổng kết">
          {renderFileList(detail.finalReportFileLink)}
          {detail.finalReportComment && (
            <Box marginTop={2} padding="8px 12px" bgcolor="#fafafa" borderRadius={4} border="solid 1px #e0e0e0">
              <Typography variant="caption" color="textSecondary"><b>Nhận xét:</b></Typography>
              <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>{detail.finalReportComment}</Typography>
            </Box>
          )}
        </SectionCard>
      )}

      {/* HĐ#3 — Nghiệm thu */}
      {renderCouncil("Hội đồng nghiệm thu (HĐ#3)", finalCouncil, INT_COUNCIL_TYPE_FINAL)}

      {/* Đơn xin dừng */}
      {hasStopRequest && (
        <SectionCard icon={<PanTool />} title="Đơn xin dừng">
          <Typography variant="body2" style={{ marginBottom: 6 }}><b>Lý do</b></Typography>
          <TextField
            fullWidth multiline minRows={3} variant="outlined" disabled
            value={detail.leaveReason || ""}
            style={{ marginBottom: 16 }}
          />
          {detail.leaveFileLink && (
            <>
              <Typography variant="body2" style={{ marginBottom: 6 }}><b>File đơn</b></Typography>
              {renderFileList(detail.leaveFileLink)}
            </>
          )}
          {detail.stopRequestComment && (
            <Box marginTop={2} padding="8px 12px" bgcolor="#fafafa" borderRadius={4} border="solid 1px #e0e0e0">
              <Typography variant="caption" color="textSecondary"><b>Quyết định admin:</b></Typography>
              <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>{detail.stopRequestComment}</Typography>
            </Box>
          )}
        </SectionCard>
      )}

      {/* Đơn xin gia hạn (1y) */}
      {hasExtension && (
        <SectionCard icon={<Update />} title="Đơn xin gia hạn (1 năm, +6 tháng)">
          {detail.extensionRequestReason && (
            <>
              <Typography variant="body2" style={{ marginBottom: 6 }}><b>Lý do</b></Typography>
              <TextField
                fullWidth multiline minRows={3} variant="outlined" disabled
                value={detail.extensionRequestReason || ""}
                style={{ marginBottom: 16 }}
              />
            </>
          )}
          {detail.extensionRequestFileLink && (
            <>
              <Typography variant="body2" style={{ marginBottom: 6 }}><b>File đơn</b></Typography>
              {renderFileList(detail.extensionRequestFileLink)}
            </>
          )}
          {detail.extendedUntilDate && (
            <Box marginTop={2}>
              <Typography variant="body2">
                <b>Đã gia hạn đến:</b> {dayjs(detail.extendedUntilDate).format("DD/MM/YYYY")}
              </Typography>
            </Box>
          )}
          {detail.extensionRequestComment && (
            <Box marginTop={2} padding="8px 12px" bgcolor="#fafafa" borderRadius={4} border="solid 1px #e0e0e0">
              <Typography variant="caption" color="textSecondary"><b>Quyết định admin:</b></Typography>
              <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>{detail.extensionRequestComment}</Typography>
            </Box>
          )}
        </SectionCard>
      )}

      {/* Comments tổng hợp (đăng ký + 3 hội đồng) */}
      {(detail.registrationComment || detail.adminReviewComment ||
        detail.selectionCouncilComment || detail.midtermCouncilComment ||
        detail.finalCouncilComment) && (
        <SectionCard icon={<CommentIcon />} title="Nhận xét các vòng duyệt">
          {detail.registrationComment && (
            <Box marginBottom={2}>
              <Typography variant="body2"><b>TĐV (trưởng đơn vị):</b></Typography>
              <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>{detail.registrationComment}</Typography>
            </Box>
          )}
          {detail.adminReviewComment && (
            <Box marginBottom={2}>
              <Typography variant="body2"><b>Phòng QLKH:</b></Typography>
              <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>{detail.adminReviewComment}</Typography>
            </Box>
          )}
          {detail.selectionCouncilComment && (
            <Box marginBottom={2}>
              <Typography variant="body2"><b>HĐ tuyển chọn:</b></Typography>
              <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>{detail.selectionCouncilComment}</Typography>
            </Box>
          )}
          {detail.midtermCouncilComment && (
            <Box marginBottom={2}>
              <Typography variant="body2"><b>HĐ tiến độ:</b></Typography>
              <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>{detail.midtermCouncilComment}</Typography>
            </Box>
          )}
          {detail.finalCouncilComment && (
            <Box marginBottom={2}>
              <Typography variant="body2"><b>HĐ nghiệm thu:</b></Typography>
              <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>{detail.finalCouncilComment}</Typography>
            </Box>
          )}
        </SectionCard>
      )}
    </>
  );
}
